import { MessageBar, MessageBarBody, Spinner, makeStyles, tokens } from '@fluentui/react-components';
import type { TableRecord } from '@powerportalspro/core';
import { usePowerPortalsPro, useT } from '@powerportalspro/react';
import {
  FileViewer,
  GridColumn,
  GridColumns,
  MainGrid,
  Section,
  SectionColumn,
  type FileSource,
} from '@powerportalspro/react-fluent';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ALL_FILES_VIEW_ID, getAllFilesView } from './FileUploadCustomViews';

// The files stored in Dataverse's ppp_file table, with a FileViewer beside the grid showing the
// one picked. Selecting a row reads that file's content (getFileInfoAsync with includeData) and
// hands it to the viewer as a base64 FileSource, named after the file so the viewer can tell from
// its extension how to show it: an image with zoom, Markdown with a Preview / Source switch, text
// and JSON colored as code, a CSV as text, a zip as a folder tree whose entries open in a viewer
// of their own. A file it can't show gets a download button instead.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.FileViewerShowcase.FileViewerShowcaseMainDemo';

const useStyles = makeStyles({
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
});

export function FileViewerShowcaseMainSample() {
  const styles = useStyles();
  const t = useT();
  const { ppp } = usePowerPortalsPro();
  // The demo's seed files, and any the signed-in user uploaded on the File Upload demo.
  const views = useMemo(() => [getAllFilesView()], []);

  const [source, setSource] = useState<FileSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Counts the selections, so a file that arrives after another was picked is dropped.
  const selection = useRef(0);

  const onSelectedRecordsChange = useCallback(
    async (records: readonly TableRecord[]) => {
      const current = ++selection.current;
      const id = records[0]?.id;
      setSource(null);
      setError(null);
      if (!id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const file = await ppp.getFileInfoAsync('ppp_file', id, 'ppp_data', true);
        if (current !== selection.current) return;
        if (file.fileData) {
          setSource({ kind: 'base64', value: file.fileData, fileName: file.fileName });
        } else {
          setError(t(`${STRINGS_BASE}.no-file`));
        }
      } catch (e) {
        if (current === selection.current) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (current === selection.current) setLoading(false);
      }
    },
    [ppp, t],
  );

  return (
    <Section borderVisible={false}>
      <SectionColumn width={2}>
        <MainGrid
          tableName="ppp_file"
          selectionMode="single"
          onSelectedRecordsChange={onSelectedRecordsChange}
          viewIds={[ALL_FILES_VIEW_ID]}
          customViewDefinitions={views}
          defaultViewId={ALL_FILES_VIEW_ID}
          minHeight="520px"
          maxHeight="520px"
        >
          {/* Just the file's name, to leave the viewer room. */}
          <GridColumns>
            <GridColumn columnName="ppp_filename" width="340px" />
          </GridColumns>
        </MainGrid>
      </SectionColumn>
      <SectionColumn width={3}>
        {loading ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : (
          <FileViewer
            source={source}
            maxHeight="460px"
            fallback={
              <MessageBar intent="info">
                <MessageBarBody>{t(`${STRINGS_BASE}.pick-a-file`)}</MessageBarBody>
              </MessageBar>
            }
          />
        )}
      </SectionColumn>
    </Section>
  );
}
